import { useState, useEffect } from 'react';
import { Menu, X, BookOpen, Clock, Award, CheckCircle, ArrowUp } from 'lucide-react';
// anchor links used for full page navigations
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import logo from "src/images/logo.png"

const coursesData = [
  {
    id: 1,
    name: 'Placement Readiness Program',
    description: 'Strengthens core programming and computer science fundamentals required for placements and technical interviews.',
    duration: '1 Month',
    skills: ['C Programming', 'Java', 'Python Basics', 'DBMS Concepts', 'Networking Basics', 'OS Fundamentals'],
    certification: 'Placement Readiness Certificate',
    syllabus: [
      'C Programming Fundamentals',
      'Java Basics and OOP',
      'Python Programming',
      'Database Management Systems',
      'Computer Networks',
      'Operating Systems'
    ]
  },
  {
    id: 2,
    name: 'MERN Stack Development',
    description: 'Full-stack web development program using MERN technologies to build scalable modern web applications.',
    duration: '2 Months',
    skills: ['Frontend Development', 'Backend Development', 'API Development', 'React', 'Node.js'],
    certification: 'Full-Stack MERN Developer Certificate',
    syllabus: [
      'HTML, CSS, JavaScript',
      'Frontend with React.js',
      'Backend with Node.js and Express.js',
      'MongoDB Database Integration',
      'REST API Development',
      'Deployment'
    ]
  },
  {
    id: 3,
    name: 'App Development – Flutter',
    description: 'Build cross-platform mobile applications using Flutter and Dart with real-time UI and backend integration.',
    duration: '2 Months',
    skills: ['Flutter', 'Dart', 'State Management', 'Mobile UI Design', 'API Integration'],
    certification: 'Flutter App Developer Certificate',
    syllabus: [
      'Flutter & Dart Basics',
      'Stateful and Stateless Widgets',
      'UI Components and Layouts',
      'State Management Techniques',
      'REST API Integration',
      'App Deployment Basics'
    ]
  },
  {
    id: 4,
    name: 'Gen AI',
    description: 'Learn Generative AI workflows including ML, NLP, LLM APIs, prompt engineering, and RAG-based fine-tuning.',
    duration: '1 Month',
    skills: ['Machine Learning', 'NLP', 'Prompt Engineering', 'LLM APIs', 'AI Deployment'],
    certification: 'Generative AI Engineer Certificate',
    syllabus: [
      'Machine Learning Fundamentals',
      'NLP Basics',
      'Prompt Engineering Techniques',
      'Working with LLM APIs',
      'RAG Workflow',
      'Fine-Tuning Models',
      'Deployment Basics'
    ]
  },
  {
    id: 5,
    name: 'Cyber Security',
    description: 'Hands-on introduction to ethical hacking, Kali Linux tools, reconnaissance, and network security techniques.',
    duration: '1 Month',
    skills: ['Network Scanning', 'Vulnerability Testing', 'Packet Analysis', 'Password Cracking', 'Recon Tools'],
    certification: 'Cyber Security Essentials Certificate',
    syllabus: [
      'Virtual Machines Setup',
      'Kali Linux Tools',
      'Wireshark Packet Analysis',
      'Hydra & John the Ripper',
      'Nmap & Scanning Techniques',
      'Reconnaissance Tools'
    ]
  },
  {
    id: 6,
    name: 'Data Analyst Program',
    description: 'Master data analysis, visualization, dashboards, and business intelligence tools used in the industry.',
    duration: '1 Month',
    skills: ['Excel', 'Tableau', 'KNIME', 'Power BI', 'Data Cleaning'],
    certification: 'Data Analyst Certificate',
    syllabus: [
      'Excel for Data Cleaning',
      'Visualization with Tableau',
      'Data Workflows in KNIME',
      'Business Dashboards with Power BI',
      'Analytics Case Studies'
    ]
  },
  {
    id: 7,
    name: 'DevOps Engineer Program',
    description: 'Learn automation, CI/CD pipelines, containerization, and cloud deployment essential for DevOps roles.',
    duration: '1 Month',
    skills: ['Git', 'Jenkins', 'Docker', 'Linux Commands', 'Azure Cloud'],
    certification: 'DevOps Engineer Certificate',
    syllabus: [
      'Version Control with Git',
      'CI/CD with Jenkins',
      'Docker Containerization',
      'Linux Command Line',
      'Deploying on Azure'
    ]
  },
  {
    id: 8,
    name: 'AR/VR Development',
    description: 'Build immersive applications using Unity and ARCore with basic 3D interaction concepts.',
    duration: '1 Month',
    skills: ['Unity Development', 'ARCore', '3D Interaction', 'Basic Game Design'],
    certification: 'AR/VR Developer Certificate',
    syllabus: [
      'Unity Basics',
      'ARCore Setup and Development',
      '3D Objects & Interactions',
      'Scene Design Fundamentals'
    ]
  },
  {
    id: 9,
    name: 'Robotics with PCB Design',
    description: 'Learn electronics, microcontrollers, and PCB designing to build basic robotic circuits.',
    duration: '1 Month',
    skills: ['Electronics Basics', 'Microcontroller Programming', 'Circuit Design', 'PCB Designing'],
    certification: 'Robotics & PCB Design Certificate',
    syllabus: [
      'Electronic Components',
      'Basic Electronic Concepts',
      'Microcontroller Introduction',
      'Programming Basics',
      'PCB Design Fundamentals'
    ]
  },
  {
    id: 10,
    name: 'UI/UX Design',
    description: 'Create stunning user interfaces and experiences with modern design tools.',
    duration: '1 Month',
    skills: ['Flutter UI', 'Canva', 'Design Principles', 'Prototyping'],
    certification: 'UI/UX Designer Certificate',
    syllabus: [
      'Design Principles',
      'Flutter UI Basics',
      'Canva for Design',
      'Prototyping & Testing'
    ]
  }
];

const AllCourses = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloating, setShowFloating] = useState(false);

  const openModal = (course) => {
    setSelectedCourse(course);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedCourse(null), 300);
  };

  // Ensure mobile menu closes when modal opens
  useEffect(() => {
    if (modalOpen && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [modalOpen, mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloating(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);


  return (
    <div>
      {/* Header - hide while modal is open */}
      {!modalOpen && (
        <header className="header" aria-hidden={modalOpen}>
          <nav className="nav-container">
            <a href="/" className="logo" data-testid="logo-link">
              <img src={logo} alt="PK Placement Services Logo" className="logo-img" />
              <span>PK Placement Services</span>
            </a>
            <ul className="nav-links">
              <li><a href="/" data-testid="nav-home">Home</a></li>
              <li><a href="/" data-testid="nav-courses">Contact</a></li>
              <li><a href="/team" data-testid="nav-team">Team</a></li>
            </ul>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
            {mobileMenuOpen && (
              <div className="mobile-nav active">
                <ul>
                  <li><a href="/" onClick={() => setMobileMenuOpen(false)}>Home</a></li>
                  <li><a href="/" onClick={() => setMobileMenuOpen(false)}>Contact</a></li>
                  <li><a href="/team" onClick={() => setMobileMenuOpen(false)}>Team</a></li>
                </ul>
              </div>
            )}
          </nav>
        </header>
      )}

      {/* Courses Section */}
      <section className="section" style={{ paddingTop: '8rem' }} data-testid="all-courses-section">
        <h2 className="section-title" data-testid="all-courses-title">All Courses</h2>
        <div className="courses-grid">
          {coursesData.map((course) => (
            <div key={course.id} className="course-card" data-testid={`course-card-${course.id}`}>
              <div className="course-content">
                <h3>{course.name}</h3>
                <p>{course.description}</p>
                <button
                  onClick={() => openModal(course)}
                  className="btn-know-more"
                  data-testid={`know-more-btn-${course.id}`}
                >
                  Know More
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Course Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl my-2 max-h-[90vh] overflow-y-auto" data-testid="course-modal">
          {selectedCourse && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-blue-900" data-testid="modal-course-title">
                  {selectedCourse.name}
                </DialogTitle>
                <DialogDescription className="text-gray-600 mt-2" data-testid="modal-course-description">
                  {selectedCourse.description}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Duration */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock size={20} color="#2563eb" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="font-semibold text-gray-800" data-testid="modal-duration">{selectedCourse.duration}</p>
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <BookOpen size={20} color="#2563eb" />
                    </div>
                    <h4 className="font-semibold text-gray-800">Skills You'll Learn</h4>
                  </div>
                  <div className="flex flex-wrap gap-2 ml-13" data-testid="modal-skills">
                    {selectedCourse.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Syllabus */}
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <CheckCircle size={20} color="#2563eb" />
                    </div>
                    <h4 className="font-semibold text-gray-800">Course Syllabus</h4>
                  </div>
                  <ul className="space-y-2 ml-13" data-testid="modal-syllabus">
                    {selectedCourse.syllabus.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Certification */}
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Award size={20} color="#2563eb" />
                    </div>
                    <h4 className="font-semibold text-gray-800">Certification</h4>
                  </div>
                  <p className="text-gray-700 ml-13" data-testid="modal-certification">{selectedCourse.certification}</p>
                </div>

                {/* Enroll Button */}
                {/* <div className="pt-4">
                  <a
                    href="https://docs.google.com/forms/d/e/1FAIpQLSeYQRWbaaxAJdhYytRFQijQVMCFacSpP6wi6F_aJredV0htmQ/viewform?usp=dialog"
                    className="btn-primary inline-block text-center w-full"
                    onClick={closeModal}
                    data-testid="modal-enroll-btn"
                    target="_blank"
                  >
                    Enroll Now
                  </a>
                </div> */}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Quick Links</h4>
            <div className="flex">
              <ul className="footer-links flex flex-col items-start">
                <li><a href="/">Home</a></li>
                <li><a href="/team">About</a></li>
              </ul>

              <ul className="footer-links px-10 flex flex-col items-start">
                <li><a href="/">Contact</a></li>
                {/* <li><a href="/terms-and-conditions">Terms & Conditions</a></li> */}
              </ul>
            </div>
          </div>

          <div className="footer-section">
            <h4>Connect With Us</h4>
            <div className="social-links flex flex-col">
              {/* WhatsApp */}
              <div className="flex items-center">
                <a
                  href="https://wa.me/+918056175066"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-whatsapp"
                >
                  <i className="fab fa-whatsapp"></i>
                </a>
                <span className='px-2 cursor-pointer' onClick={() => window.open("https://wa.me/+918056175066", "_blank")}>+91 80561 75066</span>
              </div>
              {/* Email */}
              <div className="flex items-center">
                <a
                  href="mailto:forpkqueries@gmail.com"
                  rel="noopener noreferrer"
                  data-testid="social-email"
                >
                  <i className="fas fa-envelope"></i>
                </a>
                <span className='px-2 cursor-pointer' onClick={() => window.open("mailto:forpkqueries@gmail.com", "_blank")}>forpkqueries@gmail.com</span>
              </div>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 PK Placement Services. All rights reserved.</p>
        </div>
      </footer>

      {/* Floating Button */}
      {showFloating && (
        <button
          className="floating-btt-btn"
          data-testid="floating-btt-btn"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Back to Top"
        >
          <ArrowUp className="btt-icon" />
        </button>
      )}
    </div>
  );
};

export default AllCourses;